import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { UserPlus, X, Info } from "lucide-react";
import Modal from "components/common/Modal";

const InviteMemberModal = ({ show, onClose, onInvite, workspaceName }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coach");

  const handleClose = () => {
    setEmail("");
    setRole("coach");
    onClose();
  };

  const handleInvite = () => {
    if (!email.trim()) return;
    onInvite?.({ email: email.trim(), role });
    handleClose();
  };

  const isValidEmail = email.trim().length > 0 && email.includes("@");

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <UserPlus size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.inviteMemberModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              <Trans
                i18nKey="workspace.inviteMemberModal.subtitle"
                values={{
                  name:
                    workspaceName || t("workspace.addTeamModal.yourWorkspace"),
                }}
                components={{ bold: <span className="font-medium" /> }}
              />
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Email Field */}
        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.inviteMemberModal.emailLabel")}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("workspace.inviteMemberModal.emailPlaceholder")}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
            style={{
              boxShadow: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E07A3A";
              e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Role Field */}
        <div className="mb-7">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.inviteMemberModal.roleLabel")}
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] outline-none transition-all duration-150"
            style={{
              boxShadow: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E07A3A";
              e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
          >
            <option value="coach">
              {t("workspace.inviteMemberModal.roles.coach")}
            </option>
            <option value="admin">
              {t("workspace.inviteMemberModal.roles.admin")}
            </option>
          </select>
        </div>

        {/* Info Box */}
        <div
          className="flex gap-3 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <Info size={20} className="mt-0.5 flex-shrink-0 text-[#E07A3A]" />
          <p className="text-[13px] leading-relaxed text-[#6B7280]">
            {t("workspace.inviteMemberModal.info")}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleInvite}
          disabled={!isValidEmail}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            isValidEmail
              ? {
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  boxShadow: "0 2px 8px rgba(224, 122, 58, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {t("workspace.inviteMemberModal.submit")}
        </button>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
